import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Receipt } from "lucide-react";
import { get, post, toastError } from "@/lib/api";
import { nok, fmtDate, todayISO } from "@/lib/format";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet";
import { PageHeader, Toolbar, FilterToggle, SearchInput } from "@/components/common/Page";
import { DataTable } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { EmptyState } from "@/components/common/States";
import { CustomerSelect, ProjectSelect } from "@/components/common/Pickers";
import { Money, DueCell } from "@/components/finance/FinanceBits";
import { LinesEditor, emptyLine } from "@/components/finance/LinesEditor";
import { compactKr } from "@/components/finance/money";

const FILTERS = [
  { id: "all", label: "Alle", status: null },
  { id: "open", label: "Åpne", status: "open" },
  { id: "forfalt", label: "Forfalt", status: "forfalt" },
  { id: "utkast", label: "Utkast", status: "utkast,godkjent" },
  { id: "til_godkjenning", label: "Til godkjenning", status: "til_godkjenning" },
  { id: "betalt", label: "Betalt", status: "betalt" },
  { id: "kreditnota", label: "Kreditnotaer", kind: "kreditnota" },
];

export default function InvoicesPage() {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const { can } = useAuth();
  const [q, setQ] = useState("");
  const filterId = sp.get("status") === "open" ? "open" : sp.get("kind") === "kreditnota" ? "kreditnota" : FILTERS.find((f) => f.status === sp.get("status"))?.id || "all";
  const filter = FILTERS.find((f) => f.id === filterId) || FILTERS[0];
  const customerId = sp.get("customer_id");
  const projectId = sp.get("project_id");
  const contractId = sp.get("contract_id");
  const params = { ...(filter.status ? { status: filter.status } : {}), ...(filter.kind ? { kind: filter.kind } : {}), ...(customerId ? { customer_id: customerId } : {}), ...(projectId ? { project_id: projectId } : {}), ...(contractId ? { contract_id: contractId } : {}) };
  const list = useQuery({ queryKey: ["finance", "invoices", params], queryFn: () => get("/finance/invoices", params) });
  const all = useQuery({ queryKey: ["finance", "invoices", "open-kpi"], queryFn: () => get("/finance/invoices", { status: "open,utkast,til_godkjenning,godkjent" }) });

  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (list.data || []).filter((i) => !s || [i.invoice_number, i.title, i.customer_name, i.project_name, i.po_number, i.customer_reference].some((x) => (x || "").toLowerCase().includes(s)));
  }, [list.data, q]);

  const kpi = useMemo(() => {
    const a = all.data || [];
    const open = a.filter((i) => ["sendt", "forfalt", "delvis_betalt"].includes(i.status));
    return {
      open: open.reduce((s, i) => s + (i.outstanding || 0), 0),
      openCount: open.length,
      overdue: open.filter((i) => i.status === "forfalt").reduce((s, i) => s + (i.outstanding || 0), 0),
      overdueCount: open.filter((i) => i.status === "forfalt").length,
      drafts: a.filter((i) => ["utkast", "godkjent"].includes(i.status)).length,
      approvals: a.filter((i) => i.status === "til_godkjenning").length,
    };
  }, [all.data]);

  const setFilter = (f) => {
    const n = new URLSearchParams(sp);
    n.delete("status");
    n.delete("kind");
    if (f.status) n.set("status", f.status === "open" ? "open" : f.status);
    if (f.kind) n.set("kind", f.kind);
    setSp(n, { replace: true });
  };
  const clearScope = () => {
    const n = new URLSearchParams(sp);
    ["customer_id", "project_id", "contract_id"].forEach((k) => n.delete(k));
    setSp(n, { replace: true });
  };
  const newOpen = sp.get("ny") === "1";
  const setNewOpen = (v) => {
    const n = new URLSearchParams(sp);
    if (v) n.set("ny", "1");
    else n.delete("ny");
    setSp(n, { replace: true });
  };

  const columns = [
    { key: "invoice_number", header: "Faktura", render: (r) => (
        <div className="min-w-0">
          <div className="font-medium tabular">{r.invoice_number}</div>
          <div className="max-w-[320px] truncate text-[12px] text-muted-foreground">{r.title}</div>
        </div>
      ) },
    { key: "customer_name", header: "Kunde", render: (r) => <span className="whitespace-nowrap">{r.customer_name}</span> },
    { key: "project_name", header: "Prosjekt", className: "hidden lg:table-cell", headClassName: "hidden lg:table-cell", render: (r) => <span className="block max-w-[240px] truncate text-muted-foreground">{r.project_name || "–"}</span> },
    { key: "issue_date", header: "Dato", className: "hidden md:table-cell", headClassName: "hidden md:table-cell", render: (r) => <span className="tabular">{fmtDate(r.issue_date)}</span> },
    { key: "due_date", header: "Forfall", render: (r) => (r.kind === "kreditnota" ? <span className="text-muted-foreground">–</span> : <DueCell date={r.due_date} days={r.days_overdue} />) },
    { key: "total", header: "Beløp inkl. mva", align: "right", render: (r) => <Money v={r.kind === "kreditnota" ? -r.total : r.total} /> },
    { key: "outstanding", header: "Utestående", align: "right", render: (r) => <Money v={r.outstanding} muted={!r.outstanding} className={cn(r.status === "forfalt" && r.outstanding > 0 && "font-medium text-rose-600 dark:text-rose-400")} /> },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} entity="invoice" /> },
  ];

  return (
    <div data-testid="invoices-page">
      <PageHeader
        title="Fakturaer"
        count={list.data?.length}
        subtitle={
          all.data ? (
            <span className="tabular">
              {compactKr(kpi.open)} utestående på {kpi.openCount} fakturaer
              {kpi.overdue > 0 && <> · <span className="text-rose-600 dark:text-rose-400">{compactKr(kpi.overdue)} forfalt ({kpi.overdueCount})</span></>}
              {kpi.drafts > 0 && <> · {kpi.drafts} utkast</>}
              {kpi.approvals > 0 && <> · {kpi.approvals} til godkjenning</>}
            </span>
          ) : null
        }
        actions={
          can("finance:write") && (
            <Button onClick={() => setNewOpen(true)} data-testid="invoices-new-button">
              <Plus /> Ny faktura
            </Button>
          )
        }
        testId="invoices-header"
      />
      <Toolbar className="mt-5" right={<SearchInput value={q} onChange={setQ} placeholder="Søk nummer, tittel, kunde, PO…" testId="invoices-search" />}>
        {FILTERS.map((f) => (
          <FilterToggle key={f.id} pressed={filter.id === f.id} onClick={() => setFilter(f)} testId={`invoices-filter-${f.id}`}>
            {f.label}
          </FilterToggle>
        ))}
        {(customerId || projectId || contractId) && (
          <button type="button" onClick={clearScope} className="ml-1 inline-flex h-8 items-center rounded-md bg-primary/10 px-2.5 text-[12px] font-medium text-primary transition-colors duration-150 hover:bg-primary/15" data-testid="invoices-clear-scope">
            Filtrert på {contractId ? "kontrakt" : projectId ? "prosjekt" : "kunde"} ×
          </button>
        )}
      </Toolbar>
      <DataTable
        className="mt-4"
        columns={columns}
        rows={rows}
        isLoading={list.isLoading}
        error={list.error}
        onRetry={list.refetch}
        onRowClick={(r) => navigate(`/okonomi/fakturaer/${r.id}`)}
        rowTestId={(r) => `invoice-row-${r.invoice_number}`}
        rowClassName={(r) => (r.status === "forfalt" ? "bg-rose-500/[0.03]" : undefined)}
        empty={<EmptyState icon={Receipt} title="Ingen fakturaer" description={filter.id === "all" ? "Opprett en faktura eller generer fra en kontrakt." : `Ingen fakturaer med status «${filter.label.toLowerCase()}».`} action={can("finance:write") && filter.id === "all" ? <Button size="sm" onClick={() => setNewOpen(true)}>Ny faktura</Button> : null} />}
        testId="invoices-table"
      />
      <NewInvoiceSheet open={newOpen} onOpenChange={setNewOpen} defaults={{ customer_id: customerId, project_id: projectId }} onCreated={(inv) => navigate(`/okonomi/fakturaer/${inv.id}`)} />
    </div>
  );
}

/** Manual invoice (leie-perioder lages normalt fra kontrakten). */
export const NewInvoiceSheet = ({ open, onOpenChange, defaults = {}, onCreated }) => {
  const qc = useQueryClient();
  const [form, setForm] = useState({ customer_id: null, project_id: null, title: "", issue_date: todayISO(), payment_terms_days: "", customer_reference: "", po_number: "", notes: "", lines: [emptyLine(false)] });
  useEffect(() => {
    if (open) setForm((f) => ({ ...f, customer_id: defaults.customer_id || f.customer_id, project_id: defaults.project_id || f.project_id }));
  }, [open, defaults.customer_id, defaults.project_id]);
  const create = useMutation({
    mutationFn: () =>
      post("/finance/invoices", {
        ...form,
        payment_terms_days: form.payment_terms_days ? Number(form.payment_terms_days) : null,
        lines: form.lines.filter((l) => l.description.trim()).map((l) => ({ kind: l.kind, description: l.description, quantity: Number(l.quantity) || 0, unit: l.unit, unit_price: Number(l.unit_price) || 0 })),
      }),
    onSuccess: (inv) => {
      toast.success(`Fakturautkast ${inv.invoice_number} opprettet`, { description: nok(inv.total) + " inkl. mva" });
      qc.invalidateQueries({ queryKey: ["finance"] });
      onOpenChange(false);
      onCreated?.(inv);
    },
    onError: (e) => toastError(e, "Kunne ikke opprette faktura"),
  });
  const valid = form.customer_id && form.lines.some((l) => l.description.trim() && Number(l.unit_price) > 0);
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-[720px] max-w-[96vw] flex-col gap-0 p-0" data-testid="new-invoice-sheet">
        <SheetHeader className="border-b px-6 py-4">
          <SheetTitle>Ny faktura</SheetTitle>
          <SheetDescription>Opprettes som utkast. Løpende leie faktureres normalt fra kontrakten – bruk dette for engangslinjer, tillegg og manuelle fakturaer.</SheetDescription>
        </SheetHeader>
        <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5 scrollbar-thin">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Kunde</Label>
              <CustomerSelect value={form.customer_id} onChange={(v) => setForm({ ...form, customer_id: v })} testId="new-invoice-customer" />
            </div>
            <div className="space-y-1.5">
              <Label>Prosjekt</Label>
              <ProjectSelect value={form.project_id} onChange={(v) => setForm({ ...form, project_id: v })} testId="new-invoice-project" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="inv-title">Tittel</Label>
              <Input id="inv-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="F.eks. Ekstra transport Mongstad – september" data-testid="new-invoice-title" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="inv-date">Fakturadato</Label>
              <Input id="inv-date" type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} data-testid="new-invoice-date" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="inv-terms">Betalingsfrist (dager)</Label>
              <Input id="inv-terms" type="number" value={form.payment_terms_days} onChange={(e) => setForm({ ...form, payment_terms_days: e.target.value })} placeholder="Kundens standard" data-testid="new-invoice-terms" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="inv-ref">Deres referanse</Label>
              <Input id="inv-ref" value={form.customer_reference} onChange={(e) => setForm({ ...form, customer_reference: e.target.value })} data-testid="new-invoice-reference" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="inv-po">Bestillingsnummer (PO)</Label>
              <Input id="inv-po" value={form.po_number} onChange={(e) => setForm({ ...form, po_number: e.target.value })} data-testid="new-invoice-po" />
            </div>
          </div>
          <div>
            <Label className="mb-2 block">Linjer</Label>
            <LinesEditor lines={form.lines} onChange={(lines) => setForm({ ...form, lines })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="inv-notes">Melding på faktura</Label>
            <Textarea id="inv-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="new-invoice-notes" />
          </div>
        </div>
        <SheetFooter className="border-t px-6 py-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="new-invoice-cancel">
            Avbryt
          </Button>
          <Button onClick={() => create.mutate()} disabled={!valid || create.isPending} data-testid="new-invoice-submit">
            Opprett utkast
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
